// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** The bar, collars and unit a lifter is loading with. */
export const kits = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededKit = demoData([
  { id: -1, name: 'Kit 1', unit: 'kg', barWeight: 1, collarWeight: 1 },
  { id: -2, name: 'Kit 2', unit: 'lb', barWeight: 2, collarWeight: 2 },
  { id: -3, name: 'Kit 3', unit: 'kg', barWeight: 3, collarWeight: 3 },
]);
watch(seededKit, (rows) => {
  if (rows.length && !kits.value.length) kits.value = [...rows];
}, { immediate: true });

export const kitsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'kit');
  if (mine.length) kits.value = mine;
  return kits.value;
});

export function addKit(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'kit',
    name: input.name ?? '',
    unit: input.unit ?? '',
    barWeight: input.barWeight ?? 0,
    collarWeight: input.collarWeight ?? 0,
  };
  kits.value = [row, ...kits.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveKit(row) {
  kits.value = kits.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeKit(id) {
  kits.value = kits.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findKit = (id) =>
  kits.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentKit = computed(() => kits.value.slice(0, 3));
export const kitsTitle = (row) => row?.name;

/** A plate size in the kit and how many pairs exist. */
export const plates = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededPlate = demoData([
  { id: -1, kit: -1, weight: 1, pairs: 1, colour: 'Sample colour' },
  { id: -2, kit: -2, weight: 2, pairs: 2, colour: 'Sample colour' },
  { id: -3, kit: -3, weight: 3, pairs: 3, colour: 'Sample colour' },
]);
watch(seededPlate, (rows) => {
  if (rows.length && !plates.value.length) plates.value = [...rows];
}, { immediate: true });

export const platesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'plate');
  if (mine.length) plates.value = mine;
  return plates.value;
});

export function addPlate(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'plate',
    kit: input.kit ?? '',
    weight: input.weight ?? 0,
    pairs: input.pairs ?? 0,
    colour: input.colour ?? '',
  };
  plates.value = [row, ...plates.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function savePlate(row) {
  plates.value = plates.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removePlate(id) {
  plates.value = plates.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findPlate = (id) =>
  plates.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentPlate = computed(() => plates.value.slice(0, 3));
export const platesTitle = (row) => row?.colour;

/** A movement with today's top set and where its warm-ups start. */
export const lifts = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededLift = demoData([
  { id: -1, name: 'Lift 1', kind: 'squat', topSet: 1, topSetReps: 1, startWeight: 1, startReps: 1, kit: -1 },
  { id: -2, name: 'Lift 2', kind: 'bench', topSet: 2, topSetReps: 2, startWeight: 2, startReps: 2, kit: -2 },
  { id: -3, name: 'Lift 3', kind: 'deadlift', topSet: 3, topSetReps: 3, startWeight: 3, startReps: 3, kit: -3 },
]);
watch(seededLift, (rows) => {
  if (rows.length && !lifts.value.length) lifts.value = [...rows];
}, { immediate: true });

export const liftsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'lift');
  if (mine.length) lifts.value = mine;
  return lifts.value;
});

export function addLift(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'lift',
    name: input.name ?? '',
    kind: input.kind ?? '',
    topSet: input.topSet ?? 0,
    topSetReps: input.topSetReps ?? 0,
    startWeight: input.startWeight ?? 0,
    startReps: input.startReps ?? 0,
    kit: input.kit ?? '',
  };
  lifts.value = [row, ...lifts.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveLift(row) {
  lifts.value = lifts.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeLift(id) {
  lifts.value = lifts.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findLift = (id) =>
  lifts.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentLift = computed(() => lifts.value.slice(0, 3));
export const liftsTitle = (row) => row?.name;

/** One warm-up in a lift's scheme, as a share of the top set. */
export const rampSteps = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededRampStep = demoData([
  { id: -1, lift: -1, position: 1, percent: 1, reps: 1 },
  { id: -2, lift: -2, position: 2, percent: 2, reps: 2 },
  { id: -3, lift: -3, position: 3, percent: 3, reps: 3 },
]);
watch(seededRampStep, (rows) => {
  if (rows.length && !rampSteps.value.length) rampSteps.value = [...rows];
}, { immediate: true });

export const rampStepsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'ramp-step');
  if (mine.length) rampSteps.value = mine;
  return rampSteps.value;
});

export function addRampStep(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'ramp-step',
    lift: input.lift ?? '',
    position: input.position ?? 0,
    percent: input.percent ?? 0,
    reps: input.reps ?? 0,
  };
  rampSteps.value = [row, ...rampSteps.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveRampStep(row) {
  rampSteps.value = rampSteps.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeRampStep(id) {
  rampSteps.value = rampSteps.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findRampStep = (id) =>
  rampSteps.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentRampStep = computed(() => rampSteps.value.slice(0, 3));
export const rampStepsTitle = (row) => row?.id;

/** A set actually lifted, optionally with a video pointer. */
export const attempts = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededAttempt = demoData([
  { id: -1, lift: -1, weight: 1, reps: 1, performedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), videoAssetId: 'Sample videoAssetId', videoSource: 'recorded', note: 'A short note for Attempt 1, long enough to show wrapping.' },
  { id: -2, lift: -2, weight: 2, reps: 2, performedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), videoAssetId: 'Sample videoAssetId', videoSource: 'linked', note: 'A short note for Attempt 2, long enough to show wrapping.' },
  { id: -3, lift: -3, weight: 3, reps: 3, performedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), videoAssetId: 'Sample videoAssetId', videoSource: 'recorded', note: 'A short note for Attempt 3, long enough to show wrapping.' },
]);
watch(seededAttempt, (rows) => {
  if (rows.length && !attempts.value.length) attempts.value = [...rows];
}, { immediate: true });

export const attemptsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'attempt');
  if (mine.length) attempts.value = mine;
  return attempts.value;
});

export function addAttempt(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'attempt',
    lift: input.lift ?? '',
    weight: input.weight ?? 0,
    reps: input.reps ?? 0,
    performedAt: input.performedAt ?? new Date().toISOString(),
    videoAssetId: input.videoAssetId ?? '',
    videoSource: input.videoSource ?? '',
    note: input.note ?? '',
  };
  attempts.value = [row, ...attempts.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveAttempt(row) {
  attempts.value = attempts.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeAttempt(id) {
  attempts.value = attempts.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findAttempt = (id) =>
  attempts.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentAttempt = computed(() => attempts.value.slice(0, 3));
export const attemptsTitle = (row) => row?.videoAssetId;
